<template>
  <CustomDropdown
    width="w-fit"
    max-height="300px"
    gap="5px"
    :position="position"
    :relative-to-viewport="true"
    dropdownCSS="p-0"
    @open="handleDropdownOpen"
  >
    <template #trigger>
      <div class="flex flex-row items-center gap-1 cursor-pointer text-[13px]">
        <ThemeImage
          :icon="imageNames.CURLY_BRACE_GREY"
          alt="personalize_icon"
          class="size-4"
        />
        <span class="text-color">Personalize</span>
      </div>
    </template>

    <template #default>
      <div class="flex flex-col max-h-[300px]">
        <div class="flex flex-col py-3 px-2 border_bottom">
          <CustomInput
            ref="searchInputRef"
            v-model="searchQuery"
            format="text"
            placeholder="Search variables"
            type="primary"
            :icon="imageNames.SEARCH_VARIANT1"
            alt="search_icon"
            auto-focus
          />
        </div>

        <div
          class="flex flex-col pl-2 py-2 overflow-y-scroll custom_scrollbar max-h-[200px]"
        >
          <div
            v-for="variable in variables"
            :key="variable.id"
            class="dropdown_item flex-shrink-0 flex items-center flex-row px-3 py-2 rounded-lg cursor-pointer"
            @click="handleVariableSelect(variable.variable_name)"
          >
            <span class="text-sm text_color">{{ variable.variable_name }}</span>
          </div>

          <div
            v-if="isLoading"
            class="flex flex-col items-center justify-center py-6 px-4"
          >
            <span class="text-sm secondary_color text-center">Loading...</span>
          </div>

          <div
            v-else-if="!variables.length"
            class="flex flex-col items-center justify-center py-6 px-4"
          >
            <ThemeImage
              :icon="imageNames.EMPTY_SCREEN_GREY"
              alt="empty_icon"
              class="size-16"
            />
            <span class="text-sm secondary_color text-center mt-2">
              No variables found
            </span>
          </div>
        </div>
      </div>
    </template>
  </CustomDropdown>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import { debounce } from "lodash";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { fetchDynamicVariablesForAgent } from "@/src/utils/voiceAgentUtils.js";

const props = defineProps({
  agentId: {
    type: [String, Number],
    required: true,
  },
  position: {
    type: String,
    default: "end",
  },
});

const emit = defineEmits(["apply-personalization"]);

const searchQuery = ref("");
const variables = ref([]);
const isLoading = ref(false);
const searchInputRef = ref(null);

const fetchVariables = async (query = "") => {
  try {
    if (!props.agentId) {
      variables.value = [];
      return;
    }

    isLoading.value = true;
    const result = await fetchDynamicVariablesForAgent(props.agentId, query);
    variables.value = result || [];
  } catch (error) {
    console.error("Error fetching dynamic variables:", error);
    variables.value = [];
  } finally {
    isLoading.value = false;
  }
};

const debouncedFetchVariables = debounce((query) => {
  fetchVariables(query);
}, 300);

const handleVariableSelect = (variableName) => {
  try {
    if (variableName) {
      emit("apply-personalization", variableName);
    }
  } catch (error) {
    console.error("Error handling variable select:", error);
  }
};

const handleDropdownOpen = async () => {
  try {
    await fetchVariables(searchQuery.value);
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } catch (error) {
    console.error("Error on dropdown open:", error);
  }
};

const refreshVariables = async () => {
  try {
    await fetchVariables(searchQuery.value);
  } catch (error) {
    console.error("Error refreshing variables:", error);
  }
};

watch(searchQuery, (newQuery) => {
  debouncedFetchVariables(newQuery);
});

onMounted(() => {
  if (props.agentId) {
    fetchVariables();
  }
});

onBeforeUnmount(() => {
  debouncedFetchVariables.cancel();
});

defineExpose({
  refreshVariables,
});
</script>
